/**
 * Ec2CompactRow — compact EC2 instance row for Stack View (BR-U5-09 through BR-U5-11).
 *
 * Compact state: health badge + instanceId + state + CPU% on one line.
 * Expanded state: adds heap utilisation and health probe details.
 *
 * Keyboard accessible: Enter/Space toggles the expanded panel (NFR-A11Y-01).
 */
import { useState } from 'react';
import type { Ec2Instance } from '@/types';
import { StatusBadge } from '../primitives/StatusBadge';
import { MetricGauge } from '../primitives/MetricGauge';
import styles from './Ec2CompactRow.module.css';

export interface Ec2CompactRowProps {
  instance: Ec2Instance;
}

export function Ec2CompactRow({ instance }: Ec2CompactRowProps) {
  const [expanded, setExpanded] = useState(false);

  const toggle = () => setExpanded((prev) => !prev);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  const heapPercent = instance.jvmMetrics?.heapUtilizationPercent ?? null;
  const panelId = `ec2-detail-${instance.resourceId}`;

  return (
    <div className={styles.wrapper}>
      <div
        className={styles.row}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        aria-label={`${instance.resourceId} — ${instance.healthStatus}. Click to ${expanded ? 'collapse' : 'expand'} details.`}
      >
        <StatusBadge status={instance.healthStatus} size="sm" />
        <span className={styles.id} title={instance.resourceId}>
          {instance.resourceId}
        </span>
        <span className={styles.state}>{instance.state}</span>
        {instance.cpuUtilization !== null && (
          <span className={styles.cpu}>
            CPU: {instance.cpuUtilization.toFixed(1)}%
          </span>
        )}
        <span className={styles.chevron} aria-hidden="true">
          {expanded ? '▲' : '▼'}
        </span>
      </div>

      {expanded && (
        <div id={panelId} className={styles.detail}>
          <div className={styles.detailGrid}>
            <div>
              <span className={styles.detailLabel}>Instance type</span>
              <span>{instance.instanceType}</span>
            </div>
            <div>
              <span className={styles.detailLabel}>AZ</span>
              <span>{instance.availabilityZone}</span>
            </div>
            {instance.privateIp && (
              <div>
                <span className={styles.detailLabel}>Private IP</span>
                <span>{instance.privateIp}</span>
              </div>
            )}
            {instance.gitBranch && (
              <div>
                <span className={styles.detailLabel}>Git branch</span>
                <span>{instance.gitBranch}</span>
              </div>
            )}
          </div>

          <div className={styles.gauges}>
            <MetricGauge
              label="CPU"
              value={instance.cpuUtilization}
              warnThreshold={70}
              critThreshold={90}
            />
            <MetricGauge
              label="Memory"
              value={instance.memoryUtilization}
              warnThreshold={80}
              critThreshold={95}
            />
            {heapPercent !== null && (
              <MetricGauge
                label="Heap"
                value={heapPercent}
                warnThreshold={75}
                critThreshold={90}
              />
            )}
          </div>

          {instance.healthProbe && (
            <div className={styles.probeSection}>
              <span className={styles.detailLabel}>Last health probe</span>
              <div className={styles.probeInfo}>
                <StatusBadge status={instance.healthProbe.healthStatus} size="sm" />
                <span className={styles.probeUrl}>{instance.healthProbe.targetUrl}</span>
                {instance.healthProbe.httpStatus && (
                  <span className={styles.probeStatus}>HTTP {instance.healthProbe.httpStatus}</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
